import cn from 'classnames';
import { useCallback } from 'react';
import { Link, NavLink } from 'react-router-dom';

import useDeviceTypeDetector from '../../hooks/useDeviceTypeDetector/useDeviceTypeDetector';
import Icon from '../../ui/Icon/Icon';
import Text from '../../ui/Text/Text';
import DrawerMenu from './DrawerMenu/DrawerMenu';
import SearchInput from './SearchInput/SearchInput';

import { AppRoutes } from '../../utils/constants';

import { useAccessibility } from '../../contexts/AccessibilityContext';
import { AccessibilityModeEnum, ThemeEnum } from '../../utils/enums';
import AccessibilityHeader from '../AccessibilityHeader/AccessibilityHeader';
import { logoPathMap } from './Header.constants';
import './header.scss';

interface IHeaderProps {
  isDrawerOpen: boolean;
  onDrawerOpenChange: (open: boolean) => void;
}

function Header({ isDrawerOpen, onDrawerOpenChange }: IHeaderProps) {
  const { isDesktop, isLaptop } = useDeviceTypeDetector();
  const {
    settings: { mode, theme },
    setMode,
    setTheme,
    resetSettings,
  } = useAccessibility();

  const navLinkClassName = ({ isActive }: { isActive: boolean }) =>
    `nav-link ${isActive ? 'nav-link-active' : ''}`;

  const toggleMode = useCallback(() => {
    const isRegularMode = mode === AccessibilityModeEnum.Regular;

    if (isRegularMode) {
      setMode(AccessibilityModeEnum.Enhanced);
      setTheme(ThemeEnum.Light);
    } else {
      resetSettings();
    }
  }, [mode]);

  const isDesktopSize = isDesktop || isLaptop;

  return (
    <header className={cn('header', isDrawerOpen && 'header-drawer-open')}>
      {mode === AccessibilityModeEnum.Enhanced && <AccessibilityHeader />}
      <div className='container'>
        <div className='header-holder'>
          {!isDesktopSize && (
            <button
              className='header-hamburger-button'
              onClick={() => onDrawerOpenChange(!isDrawerOpen)}
              aria-label='Hamburger menu'
            >
              <Icon size={24} name='hamburger' asVector />
            </button>
          )}
          <Link to={AppRoutes.home} className='logo-section' onClick={() => window.scrollTo(0, 0)}>
            <img
              src={logoPathMap[theme][Number((theme === ThemeEnum.Basic && isDesktopSize) || 0)]}
              alt='Site logo'
              className='logo'
            />
          </Link>
          {isDesktopSize && (
            <nav className='nav-menu'>
              <NavLink to='/' className={navLinkClassName}>
                <Text variant='label1' fontWeight={500}>
                  Главная
                </Text>
              </NavLink>
              <NavLink to='/entrepreneur-school' className={navLinkClassName}>
                <Text variant='label1' fontWeight={500}>
                  Школа предпринимателя
                </Text>
              </NavLink>
              <NavLink to='/courses' className={navLinkClassName}>
                <Text variant='label1' fontWeight={500}>
                  Курсы
                </Text>
              </NavLink>
              <NavLink to={AppRoutes.orgInfo} className={navLinkClassName}>
                <Text variant='label1' fontWeight={500}>
                  Об организации
                </Text>
              </NavLink>
              <NavLink to='/free-consultation' className={navLinkClassName}>
                <Text variant='label1' fontWeight={500}>
                  Бесплатная консультация
                </Text>
              </NavLink>
            </nav>
          )}
          <SearchInput />
          <button
            className='accessibility-btn'
            onClick={toggleMode}
            aria-label='Toggle accessibility mode'
          >
            <Icon size={24} name={'eye'} asVector />
          </button>
          <DrawerMenu isDrawerOpen={isDrawerOpen} onClose={() => onDrawerOpenChange(false)} />
        </div>
      </div>
    </header>
  );
}

export default Header;
